import { FormInstance, message } from 'antd';
import { UploadChangeParam, UploadFile } from 'antd/es/upload';
import axios from 'axios';
import {
  DEFAULT_LANGUAGE,
  DEFAULT_ORIENTATION,
  LANGUAGE_OPTIONS,
  THUMBNAIL_UPLOAD_STATE,
} from 'constants/index';
import { contentService } from 'content/service';
import { ThumbnailInterface, UploadThumbnailInterface } from 'content/type';
import { Dispatch, SetStateAction } from 'react';
import { PosterInterface } from 'type/seasonType';

// 언어 값을 정규화하는 함수 (소문자/다양한 형식을 옵션 형식으로 변환)
const normalizeLanguage = (lang: string | undefined): string | undefined => {
  if (!lang) return undefined;

  const normalized = lang.toUpperCase();

  // 특수 케이스 처리
  const mapping: Record<string, string> = {
    KR: 'KO',
    JP: 'JA',
    ZH: 'ZH-HANS',
  };

  // 직접 매칭되는 경우
  if (LANGUAGE_OPTIONS.some(opt => opt.value === normalized)) {
    return normalized;
  }

  // 매핑이 필요한 경우
  if (mapping[normalized]) {
    return mapping[normalized];
  }

  // 기본값으로 KO 반환
  return 'KO';
};

interface ThumbnailHandlersProps {
  form: FormInstance;
  contentId: number;
  selectedThumbnail: ThumbnailInterface | null;
  setNewThumbnailList: (thumbnailList: ThumbnailInterface[]) => void;
  setUploadedThumbnailList: (thumbnailList: ThumbnailInterface[]) => void;
  setSelectedThumbnail: (thumbnail: ThumbnailInterface | null) => void;
  removedUids: string[];
  setRemovedUids: Dispatch<SetStateAction<string[]>>;
}

interface ThumbnailHandlers {
  handleChange: (info: UploadChangeParam<UploadFile<UploadThumbnailInterface>>) => void;
  handleNewThumbnailRemove: (thumbnail: ThumbnailInterface) => void;
  handleUploadedThumbnailRemove: (thumbnail: ThumbnailInterface) => void;
  handleThumbnailUpload: () => void;
  handleThumbnailSelect: (thumbnail: ThumbnailInterface | null) => void;
  handleThumbnailChange: (thumbnail: ThumbnailInterface, key: string, value: string) => void;
  handleNewThumbnailChange: (thumbnail: ThumbnailInterface, key: string, value: string) => void;
}

const createUploadThumbnailList = (
  uploadFileList: UploadFile<UploadThumbnailInterface>[]
): ThumbnailInterface[] => {
  let fileId = 0;
  return uploadFileList.map((file: UploadFile<UploadThumbnailInterface>): ThumbnailInterface => {
    const fileUrl = file.originFileObj ? URL.createObjectURL(file.originFileObj) : '';
    return {
      id: fileId++,
      uid: file.uid,
      file: fileUrl,
      is_representative: false,
      state: THUMBNAIL_UPLOAD_STATE.UPLOADING,
      originFileObj: file.originFileObj,
      orientation: DEFAULT_ORIENTATION,
      language: DEFAULT_LANGUAGE,
    };
  });
};

const handleChange =
  (props: ThumbnailHandlersProps) =>
  (info: UploadChangeParam<UploadFile<UploadThumbnailInterface>>): void => {
    const { form, setNewThumbnailList, removedUids } = props;

    // 1) form에 저장된 기존 newPosterList 꺼내오기
    const existingList: PosterInterface[] = form.getFieldValue('newThumbnailList') || [];

    // 2) 기존 리스트에 담긴 uid만 뽑아서 배열로
    const existingUids = existingList.map(p => p.uid).filter((uid): uid is string => !!uid);

    // 3) info.fileList 중, 삭제된 uid도 아니고, 기존에 이미 추가된 uid도 아닌 것만 골라내기
    const filtered = (info.fileList || []).filter(
      file => !removedUids.includes(file.uid) && !existingUids.includes(file.uid)
    );

    // 4) 새로 올라온 파일만 PosterInterface로 변환
    const uploadThumbnailList = createUploadThumbnailList(
      filtered as UploadFile<UploadThumbnailInterface>[]
    );

    // 5) 기존 리스트와 합쳐서 최종 리스트 만들기
    const combined = [...existingList, ...uploadThumbnailList];

    // 6) form과 state 동기화
    form.setFieldValue('newThumbnailList', combined);
    setNewThumbnailList(combined);
  };

const handleNewThumbnailRemove =
  (props: ThumbnailHandlersProps) =>
  (removedThumbnail: ThumbnailInterface): void => {
    const { form, setNewThumbnailList } = props;
    const newThumbnailList = form.getFieldValue('newThumbnailList') || [];

    const tempNewThumbnailList = newThumbnailList.filter(
      (thumbnail: { uid: string }) => thumbnail.uid !== removedThumbnail.uid
    );

    form.setFieldValue('newThumbnailList', tempNewThumbnailList);
    setNewThumbnailList(tempNewThumbnailList);
  };

const handleUploadedThumbnailRemove =
  (props: ThumbnailHandlersProps) =>
  (removedThumbnail: ThumbnailInterface): void => {
    const { form, setUploadedThumbnailList } = props;
    const uploadedThumbnailList = form.getFieldValue('uploadedThumbnailList') || [];

    const tempUploadedThumbnailList = uploadedThumbnailList.filter(
      (thumbnail: { id: number }) => thumbnail.id !== removedThumbnail.id
    );

    form.setFieldValue('uploadedThumbnailList', tempUploadedThumbnailList);
    setUploadedThumbnailList(tempUploadedThumbnailList);
  };

const handleThumbnailSelect =
  (props: ThumbnailHandlersProps) =>
  (newSelectedThumbnail: ThumbnailInterface | null): void => {
    const { form, selectedThumbnail, setSelectedThumbnail } = props;
    if (selectedThumbnail) {
      selectedThumbnail.is_representative = false;
    }
    if (newSelectedThumbnail) {
      newSelectedThumbnail.is_representative = true;
    }
    form.setFieldValue('selectedThumbnail', newSelectedThumbnail);
    setSelectedThumbnail(newSelectedThumbnail);
  };

const handleThumbnailUpload = (props: ThumbnailHandlersProps) => async (): Promise<void> => {
  const { form, contentId } = props;
  const loadingMessage = message.loading('Updating...', 0);
  const thumbnailList = form.getFieldValue('thumbnailList');
  const selectedThumbnail = form.getFieldValue('selectedThumbnail');
  const newThumbnailList: ThumbnailInterface[] = form.getFieldValue('newThumbnailList') || [];
  const uploadedThumbnailList: ThumbnailInterface[] =
    form.getFieldValue('uploadedThumbnailList') || [];

  // 언어 값 정규화
  const normalizedNewThumbnailList = newThumbnailList.map(thumb => ({
    ...thumb,
    language: normalizeLanguage(thumb.language),
  }));
  const normalizedUploadedThumbnailList = uploadedThumbnailList.map(thumb => ({
    ...thumb,
    language: normalizeLanguage(thumb.language),
  }));
  const normalizedSelectedThumbnail = selectedThumbnail
    ? { ...selectedThumbnail, language: normalizeLanguage(selectedThumbnail.language) }
    : null;

  // 2️⃣ 두 리스트 합쳐서 조합별 카운트 집계
  const allList = [...normalizedNewThumbnailList, ...normalizedUploadedThumbnailList];
  const comboCounts = allList.reduce<Record<string, number>>((acc, t) => {
    const key = `${t.language}-${t.orientation}`;
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  // 3️⃣ count > 1 인 조합만 골라서 메시지 출력 후 리턴
  const duplicates = Object.entries(comboCounts)
    .filter(([_, count]) => count > 1)
    .map(([combo]) => combo);

  if (duplicates.length > 0) {
    loadingMessage();
    message.error(`중복된 언어/화면비 조합: ${duplicates.join(', ')}`);
    return; // 여기서 함수를 종료해서 실제 API 호출을 막음
  }

  if (normalizedSelectedThumbnail) {
    normalizedNewThumbnailList.map((file: { id: number; is_representative: boolean }) => {
      if (file.id === normalizedSelectedThumbnail.id) {
        file.is_representative = normalizedSelectedThumbnail.is_representative;
        normalizedSelectedThumbnail.id = -1;
      }
      return file;
    });
  }
  const removeThumbnails = thumbnailList.filter(
    (uploadedThumbnail: { id: number }) =>
      !normalizedUploadedThumbnailList.some(
        (uploadThumbnail: { id: number }) => uploadThumbnail.id === uploadedThumbnail.id
      )
  );
  const changedThumbnails = normalizedUploadedThumbnailList.filter(
    (updated: ThumbnailInterface) => {
      const original = thumbnailList.find((t: ThumbnailInterface) => t.id === updated.id);
      return (
        original &&
        (original.language !== updated.language ||
          original.orientation !== updated.orientation ||
          original.is_representative !== updated.is_representative)
      );
    }
  );

  try {
    if (normalizedNewThumbnailList.length > 0) {
      await contentService.uploadThumbnail(contentId, normalizedNewThumbnailList);
    }
    if (removeThumbnails.length > 0) {
      await contentService.removeThumbnail(contentId, removeThumbnails);
    }
    if (changedThumbnails.length > 0) {
      for (const thumbnail of changedThumbnails) {
        await contentService.updateThumbnail(contentId, thumbnail);
      }
    }
    if (normalizedSelectedThumbnail && normalizedSelectedThumbnail.id !== -1) {
      await contentService.updateThumbnail(contentId, normalizedSelectedThumbnail);
    }
    loadingMessage();
    message.success('upload success');
    window.location.reload();
    form.resetFields([
      'newThumbnailList',
      'uploadedThumbnailList',
      'selectedThumbnail',
      'thumbnailList',
    ]);
  } catch (error) {
    loadingMessage();
    if (axios.isAxiosError(error) && error.response?.status === 400) {
      message.error('Failed to update : ' + JSON.stringify(error.response.data));
    }
  }
};

const handleNewThumbnailChange =
  (props: ThumbnailHandlersProps) =>
  (targetThumbnail: UploadThumbnailInterface, key: string, value: string): void => {
    const { form, setNewThumbnailList } = props;
    const newThumbnailList = form.getFieldValue('newThumbnailList') || [];

    const updatedList = newThumbnailList.map((thumbnail: UploadThumbnailInterface) => {
      if (thumbnail.uid !== targetThumbnail.uid) return thumbnail;

      // language 필드인 경우 정규화
      const normalizedValue = key === 'language' ? normalizeLanguage(value) : value;

      const updated: UploadThumbnailInterface = {
        ...thumbnail,
        [key]: normalizedValue,
      };

      return updated;
    });

    // ✅ 1. form 값에는 원본 그대로 저장 (originFileObj 유지)
    form.setFieldValue('newThumbnailList', updatedList);

    // ✅ 2. 화면 렌더링용은 file을 blob URL로 보장
    const renderList = updatedList.map((thumbnail: UploadThumbnailInterface) => ({
      ...thumbnail,
      file:
        typeof thumbnail.file !== 'string' && thumbnail.file
          ? URL.createObjectURL(thumbnail.file)
          : thumbnail.file,
    }));

    setNewThumbnailList(renderList);
  };

const handleThumbnailChange =
  (props: ThumbnailHandlersProps) =>
  (targetThumbnail: ThumbnailInterface, key: string, value: string): void => {
    const { form, setUploadedThumbnailList } = props;
    const uploadedThumbnailList = form.getFieldValue('uploadedThumbnailList') || [];

    // language 필드인 경우 정규화
    const normalizedValue = key === 'language' ? normalizeLanguage(value) : value;

    const updatedList = uploadedThumbnailList.map((thumbnail: ThumbnailInterface) =>
      thumbnail.id === targetThumbnail.id ? { ...thumbnail, [key]: normalizedValue } : thumbnail
    );

    form.setFieldValue('uploadedThumbnailList', updatedList);
    setUploadedThumbnailList(updatedList);
  };

const thumbnailHandlers = (props: ThumbnailHandlersProps): ThumbnailHandlers => ({
  handleChange: handleChange(props),
  handleNewThumbnailRemove: handleNewThumbnailRemove(props),
  handleUploadedThumbnailRemove: handleUploadedThumbnailRemove(props),
  handleThumbnailSelect: handleThumbnailSelect(props),
  handleThumbnailUpload: handleThumbnailUpload(props),
  handleThumbnailChange: handleThumbnailChange(props),
  handleNewThumbnailChange: handleNewThumbnailChange(props),
});

export default thumbnailHandlers;
